import type {Component, Editor} from 'grapesjs';
import {getComponentStyleProperty, setAutoWidthStyle, setComponentStyle, setCustomWidthStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {updateSocialLinkIconStyle} from '@EmailMakerReact/lib/email-maker-social-signup-library';
import {getAssetUrl} from '@Utils/s3.util';
import type {DropdownSelectorDescriptor} from './dropdown-selector-control.types';
import {DEFAULT_WIDTH_PERCENT, PERCENT_UNIT, WIDTH_MODE, WIDTH_PROPERTY} from './dropdown-selector-control.types';

export const BORDER_STYLE_PROPERTY = 'border-style';

const BORDER_WIDTH_PROPERTY = 'border-width';
const BORDER_COLOR_PROPERTY = 'border-color';

const BORDER_STYLE_NONE = 'none';
const BORDER_STYLE_DEFAULT = BORDER_STYLE_NONE;

/** Border-width values the content container ships as its "no border" sentinels. */
const BORDER_OFF_WIDTH_VALUES: ReadonlySet<string> = new Set(['', '0px', '0']);

/** The content container's placeholder border-color, invisible at 0px. */
const BORDER_PLACEHOLDER_COLOR = '#ffffff';

const BORDER_ENABLE_DEFAULT_WIDTH = '2px';
const BORDER_ENABLE_DEFAULT_COLOR = '#000000';

/**
 * Gives the border a visible 2px black default the first time it is enabled, so it does not
 * stay an invisible 0px/white border from the content container's no-border sentinels.
 */
function applyBorderDefaults(editor: Editor, component: Component): void {
  if (BORDER_OFF_WIDTH_VALUES.has(getComponentStyleProperty(component, BORDER_WIDTH_PROPERTY))) {
    setComponentStyle(editor, {property: BORDER_WIDTH_PROPERTY, value: BORDER_ENABLE_DEFAULT_WIDTH, component});
  }
  const color = getComponentStyleProperty(component, BORDER_COLOR_PROPERTY);
  if (color === '' || color.toLowerCase() === BORDER_PLACEHOLDER_COLOR) {
    setComponentStyle(editor, {property: BORDER_COLOR_PROPERTY, value: BORDER_ENABLE_DEFAULT_COLOR, component});
  }
}

export function isBorderStyleNonNone(component: Component): boolean {
  const raw = getComponentStyleProperty(component, BORDER_STYLE_PROPERTY);
  return raw !== '' && raw !== BORDER_STYLE_NONE;
}

export const borderStyleDescriptor: DropdownSelectorDescriptor = {
  property: BORDER_STYLE_PROPERTY,
  labelKey: 'pmwjs_border',
  defaultValue: BORDER_STYLE_DEFAULT,
  options: [
    {textKey: 'pmwjs_none', value: 'none'},
    {textKey: 'pmwjs_background_solid', value: 'solid'},
    {textKey: 'pmwjs_dashed', value: 'dashed'},
  ],
  onDropDownItemChange(component: Component, _property: string, value: string, editor: Editor): void {
    if (value !== BORDER_STYLE_NONE) {
      applyBorderDefaults(editor, component);
    }
  },
};

function getCurrentWidthPercent(component: Component): string {
  const currentWidth = getComponentStyleProperty(component, WIDTH_PROPERTY);
  return currentWidth !== '' ? currentWidth.replace(PERCENT_UNIT, '') : DEFAULT_WIDTH_PERCENT;
}

export const widthDropdownDescriptor: DropdownSelectorDescriptor = {
  property: 'button-width',
  labelKey: 'pmwjs_width',
  defaultValue: WIDTH_MODE.AUTO,
  options: [
    {textKey: 'pmwjs_auto', value: WIDTH_MODE.AUTO},
    {textKey: 'pmwjs_custom', value: WIDTH_MODE.CUSTOM},
  ],
  onDropDownItemChange(component: Component, _property: string, value: string, editor: Editor): void {
    if (value === WIDTH_MODE.AUTO) {
      setAutoWidthStyle(editor, component);
    } else if (value === WIDTH_MODE.CUSTOM) {
      setCustomWidthStyle(editor, component, getCurrentWidthPercent(component));
    }
  },
};

export const iconStyleDescriptor: DropdownSelectorDescriptor = {
  property: 'icon-style',
  labelKey: 'pmwjs_icon_style',
  defaultValue: 'original',
  options: [
    {textKey: 'pmwjs_original', value: 'original', icon: getAssetUrl('social-icons/original/social-icon-facebook.png')},
    {textKey: 'pmwjs_square', value: 'square-black', icon: getAssetUrl('social-icons/square-black/social-icon-facebook.png')},
    {textKey: 'pmwjs_circle', value: 'circle-black', icon: getAssetUrl('social-icons/circle-black/social-icon-facebook.png')},
    {textKey: 'pmwjs_line', value: 'line-black', icon: getAssetUrl('social-icons/line-black/social-icon-facebook.png')},
    {textKey: 'pmwjs_dark', value: 'line-white', icon: getAssetUrl('social-icons/line-white/social-icon-facebook.png'), iconNeedsContrastBackground: true},
  ],
  onDropDownItemChange(_component: Component, _property: string, value: string, editor: Editor): void {
    updateSocialLinkIconStyle(editor, value);
  },
};
